/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import React from "react";
import { kebabCase } from "lodash-es";
//Foundation
import { useSite } from "../../_foundation/hooks/useSite";
//UI
import {
  StyledLink,
} from "@hcl-commerce-store-sdk/react-component";
import {
  StyledMenuTypography,
  StyledMenuItem,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
 } from "components/elements";
/* import ExpandMoreIcon from "@mui/icons-material/ExpandMore"; */
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled, useTheme } from "@mui/material";

//GA360
import AsyncCall from "../../_foundation/gtm/async.service";

const MegaMenuStyles = styled("div")`
${({ theme }) => `

  .category-title {
    font-family: Inter;
    font-size: 18px;
    text-transform: none;
    font-variation-settings: 'wght' 700;
  }

  .category-subtitle {
    font-family: Inter;
    font-size: 16.5px;
    text-transform: none;
    font-variation-settings: 'wght' 600;
  }

  .non-expandable-category-subtitle {
    font-family: Inter;
    font-size: 16.5px;
    text-transform: none;...
    font-variation-settings: 'wght' 400;
  }

  .category-title-accordion {
    padding: 0px 0px 0px 16px;
  }

  .menu-item-content {
    padding: 0px 24px;
  }

  `}
`;

const MegaMenuLink = (props: any) => {
  const {
    name,
    link,
    page,
    level,
    activeMenuId,
    setActiveMenuId,
    activeParentMenuId,
    setActiveParentMenuId,
    closeMegaMenu,
    parentId,
    parentName,
    site,
  } = props;

  const onClick = () => {
    //GA360
    if (site.enableGA) AsyncCall.sendNavigationClick(parentName, name);
    closeMegaMenu();
  };

  return (
    <>
      {page.children && page.children.length > 0 ? (
        <MegaMenuColumn
          page={page}
          activeMenuId={activeMenuId}
          setActiveMenuId={setActiveMenuId}
          activeParentMenuId={activeParentMenuId}
          setActiveParentMenuId={setActiveParentMenuId}
          closeMegaMenu={closeMegaMenu}
          level={level + 1}
          parentId={parentId}
          breadcrumbs={link.state?.breadCrumbTrailEntryView ?? []}
        />
      ) : (
        <StyledLink to={link.pathname} onClick={onClick}>
          <StyledMenuItem className="menu-item-content" role="menuitem">
            <StyledMenuTypography className="non-expandable-category-subtitle">
              <span>{name}</span>
            </StyledMenuTypography>
          </StyledMenuItem>
        </StyledLink>
      )}
    </>
  );
};

interface MegaMenuColumnProps {
  page: any;
  activeMenuId: number | undefined;
  setActiveMenuId: any;
  activeParentMenuId: number | undefined;
  setActiveParentMenuId: any;
  closeMegaMenu: any;
  level: number;
  parentId: number | undefined;
  breadcrumbs?: any[];
}

/**
 * MegaMenu component
 * displays top category links in desktop/mobile view
 * @param props
 */
const MegaMenuColumn: React.FC<MegaMenuColumnProps> = (props: any) => {
  const {
    page,
    activeMenuId,
    setActiveMenuId,
    activeParentMenuId,
    setActiveParentMenuId,
    closeMegaMenu,
    level,
    breadcrumbs = [],
  } = props;
  const [expand, setExpand] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.up("sm"));
  const id = page.id;
  const { mySite } = useSite();

  const childrenList: JSX.Element[] = [];

  if (page.children && page.children.length > 0) {
    page.children.map(function (childPage: any, index: number) {
      const element = (
        <>
          <MegaMenuLink
            key={childPage.id}
            link={{
              pathname: childPage.seo?.href,
              state: {
                breadCrumbTrailEntryView: [
                  ...breadcrumbs,
                  { label: page.name, value: page.id, seo: page.seo },
                  {
                    label: childPage.name,
                    value: childPage.id,
                    seo: childPage.seo,
                  },
                ],
              },
            }}
            id={id}
            name={childPage.name}
            page={childPage}
            activeMenuId={activeMenuId}
            setActiveMenuId={setActiveMenuId}
            activeParentMenuId={activeParentMenuId}
            setActiveParentMenuId={setActiveParentMenuId}
            closeMegaMenu={closeMegaMenu}
            level={level + 1}
            parentId={id}
            parentName={props.page.name}
            site={mySite}
          />
        </>
      );
      childrenList.push(element);
      return null;
    });
  }

  const icon = useMediaQuery(theme.breakpoints.down("sm")) ? page.children ? <AddIcon style={{color: theme.palette.primary.main}} /> : "" : "";
  const iconMinimize = useMediaQuery(theme.breakpoints.down("sm")) ? page.children ? <RemoveIcon style={{color: theme.palette.primary.main}} /> : "" : "";

  const onChange = () => {
    setActiveMenuId(activeMenuId !== id ? id : null);
    setActiveParentMenuId(activeParentMenuId !== id ? id : null);
  };

  const onClick = () => {
    //GA360
    if (mySite.enableGA) {
      AsyncCall.sendNavigationClick(
        { eventAction: "Main", eventLabel: page.name },
        {
          enableUA: mySite.enableUA,
          enableGA4: mySite.enableGA4,
        }
      );
    }
    closeMegaMenu();
  };
  
  const toggleAcordion = () => {
    setExpand((prev) => !prev);
  };

  return (
    <>
      <MegaMenuStyles>
        <StyledAccordion
          testId={`mega-menu-column-${kebabCase(page.name)}`}
          elevation={0}
          square={true}
          expanded={expand}
          onChange={onChange}>
          <StyledAccordionSummary
            className={`level-${level}`}
            expandIcon={expand? iconMinimize:icon}
            onClick={toggleAcordion}
            aria-controls={`${id}bh-content`}
            id={`${id}bh-header`}>
            <StyledLink to={page.seo?.href} onClick={onClick}>
              <StyledMenuItem>
                <StyledMenuTypography
                  /* variant={level === 1 ? "overline" : "body2"} */
                  className={level === 1 ? "category-title" : "category-subtitle"}
                  id={`megamenu_department_${page.id}`}
                  title={page.name}>
                  {page.name}
                </StyledMenuTypography>
              </StyledMenuItem>
            </StyledLink>
          </StyledAccordionSummary>
          <StyledAccordionDetails className="category-title-accordion">{childrenList}</StyledAccordionDetails>
        </StyledAccordion>
      </MegaMenuStyles>
    </>
  );
};

export default MegaMenuColumn;
